import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { FileText, Download } from "lucide-react";
import { DocumentSearchParams } from "@/models/Document";
import { SUPABASE_URL, SUPABASE_ANON_KEY, getCurrentSelectedRole } from "@/services/api/apiUtils";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface DocumentExportProps {
  searchParams: DocumentSearchParams;
}

export function DocumentExport({ searchParams }: DocumentExportProps) {
  const { toast } = useToast();
  const { selectedRole } = useAuth();
  const [isExporting, setIsExporting] = useState(false);

  // Only platform users can export documents
  const isPlatformUser = selectedRole?.type === 'platform';

  if (!isPlatformUser) {
    return null;
  }

  const handleExport = async () => {
    try {
      setIsExporting(true);

      // Get auth token for authenticated request
      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token;
      
      if (!authToken) {
        toast({
          title: "Authentication error",
          description: "You need to be logged in to export documents",
          variant: "destructive"
        });
        return;
      }

      // Build query params based on current search filters
      const queryParams = new URLSearchParams();
      
      if (searchParams.search) {
        queryParams.append('search', searchParams.search);
      }
      
      if (searchParams.document_type_id) {
        queryParams.append('document_type_id', searchParams.document_type_id.toString());
      }
      
      if (searchParams.document_group_ids && searchParams.document_group_ids.length > 0) {
        searchParams.document_group_ids.forEach(id => {
          queryParams.append('document_group_ids', id.toString());
        });
      }
      
      if (searchParams.publicly_available !== undefined) {
        queryParams.append('publicly_available', searchParams.publicly_available.toString());
      }

      // Backend now handles role-based filtering automatically through permissions
      // No need to pass management_company_id, show_all, or show_none

      // Get current selected role for headers
      const currentRole = getCurrentSelectedRole();
      const headers: HeadersInit = {
        'Authorization': `Bearer ${authToken}`,
        'apikey': SUPABASE_ANON_KEY
      };

      // Add role header if available
      if (currentRole) {
        headers['X-User-Role'] = JSON.stringify(currentRole);
      }

      // Make request to export endpoint
      const response = await fetch(`${SUPABASE_URL}/documents/export?${queryParams.toString()}`, {
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to export documents');
      }
      
      // Get the CSV content from the response
      const csvContent = await response.text();
      
      // Create a blob and download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `documents_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Export successful",
        description: "Documents have been exported to CSV"
      });
    } catch (error) {
      console.error("Export error:", error);
      toast({
        title: "Export failed",
        description: error instanceof Error ? error.message : "Failed to export documents",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      className="flex items-center gap-2"
      onClick={handleExport}
      disabled={isExporting}
    >
      {isExporting ? (
        <>
          <FileText className="h-4 w-4 animate-pulse" />
          Exporting...
        </>
      ) : (
        <>
          <Download className="h-4 w-4" />
          Export Documents
        </>
      )}
    </Button>
  );
}
